import React, { useState, useEffect } from 'react'
import styles from './styles'
import {
  Typography,
  withStyles,
  InputAdornment,
  MenuItem,
  Stack,
} from '@material-ui/core'
import { CoreButton, CoreTextField } from 'src/components/core'
import { Icon } from '@iconify/react'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { useHistory, useParams } from 'react-router-dom'
import { getUserLimits } from 'src/api/limit'
import { LimitTypeEnum } from 'src/model/limit'
import LimitReachedModal from 'src/components/limit-reached-modal'
import MenuPopover from 'src/components/MenuPopover'
import useTrackerFunction from 'src/hooks/useTrackerFunction'

const sortOptions = [
  { value: 'created_date_desc', label: 'Created Date (Newest)' },
  { value: 'created_date_asc', label: 'Created Date (Oldest)' },
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
]

const CourseHeader = ({
  classes,
  onSearch,
  onSort,
  onRefresh,
  searchTerm = '',
  sortOption = 'created_date_desc',
}) => {
  const [search, setSearch] = useState(searchTerm)
  const [sortBy, setSortBy] = useState(sortOption)
  const [modalOpen, setModalOpen] = useState(false)
  const [sortMenuOpen, setSortMenuOpen] = useState(null)
  const history = useHistory()
  const { id } = useParams()
  const trackCreateCourse = useTrackerFunction('clicked_create_course', {
    type: 'independent',
  })

  // When props change, update local state
  useEffect(() => {
    setSearch(searchTerm)
  }, [searchTerm])

  useEffect(() => {
    setSortBy(sortOption)
  }, [sortOption])

  // Handle search input change
  const handleSearchChange = e => {
    const newSearch = e.target.value
    setSearch(newSearch)
    if (onSearch) onSearch(newSearch)
    if (onRefresh) onRefresh(newSearch, sortBy)
  }
  const handleRedirect = () => {
    if (isLoadingLimit) {
      setModalOpen(true)
      return
    }

    trackCreateCourse()
    history.push('/create-course-selection?collectionId=' + id)
  }

  const [isLoadingLimit, setIsLoadingLimit] = useState(true)
  const [isUserReachedCourseLimit, setIsUserReachedCourseLimit] = useState(
    false,
  )
  useEffect(() => {
    const checkUserLimits = async () => {
      try {
        setIsLoadingLimit(true)
        const response = await getUserLimits()
        let count = response.data.find(
          x => x.limit.type == LimitTypeEnum.CourseCreation,
        ).count

        let limit = response.data.find(
          x => x.limit.type == LimitTypeEnum.CourseCreation,
        ).limit.upperLimit

        count >= limit && limit != -1
          ? setIsUserReachedCourseLimit(true)
          : setIsUserReachedCourseLimit(false)
      } catch (error) {
        console.error('Error checking user limits:', error)
        setIsUserReachedCourseLimit(false)
      } finally {
        setIsLoadingLimit(false)
      }
    }
    checkUserLimits()
  }, [])

  const handleOpenSortMenu = event => {
    setSortMenuOpen(event.currentTarget)
  }

  const handleCloseSortMenu = () => {
    setSortMenuOpen(null)
  }

  const handleSortChange = value => {
    setSortBy(value)
    if (onSort) onSort(value)
    if (onRefresh) onRefresh(search, value)
    setSortMenuOpen(null)
  }

  const getCurrentSortLabel = () => {
    const option = sortOptions.find(opt => opt.value === sortBy)
    return option ? option.label : 'Sort by'
  }

  return (
    <div className={classes.root}>
      <div className={classes.headerContainer}>
        <Typography className={classes.headerText}>Mini courses</Typography>
        <CoreButton
          startIcon={<Icon icon="mdi:plus" />}
          onClick={handleRedirect}
        >
          Create mini course
        </CoreButton>
      </div>
      <div className={classes.searchContainer}>
        <CoreTextField
          className={classes.searchField}
          placeholder="Search courses..."
          value={search}
          onChange={handleSearchChange}
          marginTop={false}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="mdi:search" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <CoreTextField
            className={classes.sortField}
            value={getCurrentSortLabel()}
            size="small"
            marginTop={false}
            onClick={handleOpenSortMenu}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  {sortMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </div>

      <MenuPopover
        open={Boolean(sortMenuOpen)}
        anchorEl={sortMenuOpen}
        onClose={handleCloseSortMenu}
        sx={{
          width: '200px',
          '& .MuiMenuItem-root': {
            px: 1,
            typography: 'body2',
            borderRadius: 0.75,
            marginBottom: '4px',
          },
        }}
        disabledArrow
      >
        {sortOptions.map(option => (
          <MenuItem
            key={option.value}
            onClick={() => handleSortChange(option.value)}
            selected={sortBy === option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </MenuPopover>

      {modalOpen && (
        <LimitReachedModal
          handleCloseModal={() => setModalOpen(false)}
          isModalOpen={modalOpen}
        />
      )}
    </div>
  )
}

export default withStyles(styles)(CourseHeader)
